import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import * as style from "../styles.module.scss";
import { Button, TextFieldGroup } from "../../../common";
import { AddressSelectList, ContactPostcode, DOB } from "../../BasicInfo";
import ManualAddress from "../../BasicInfo/Contacts/ManualAddressForm";

const PersonalDetails = ({
  handleSubmitPersonalDetails,
  navigateBackPath,
  personalDetailsState,
  setPersonalDetailsState,
  personalDetailsErrors,
  canFindAddress,
  getMatchedAddress,
  postcodeError,
  showManualAddress,
  shouldShowAddresses,
  hideManualAddress,
  matchedAddressList,
  onAddressSelect,
}) => {
  const {
    firstName,
    lastName,
    birthDay,
    birthMonth,
    birthYear,
    mobileTelephone,
    addressLine1,
    addressLine2,
    addressLine3,
    town,
    county,
    postcode,
    country,
    isManualAddress,
    selectedAddressIndex,
  } = personalDetailsState;

  const isDOBInvalid =
    (personalDetailsErrors.birthDay &&
      personalDetailsErrors.birthDay.length > 0) ||
    (personalDetailsErrors.birthMonth &&
      personalDetailsErrors.birthMonth.length > 0) ||
    (personalDetailsErrors.birthYear &&
      personalDetailsErrors.birthYear.length > 0);

  const isUnder16 =
    isDOBInvalid &&
    personalDetailsErrors.birthYear &&
    personalDetailsErrors.birthYear[0] === "Birth year not before";

  const dobValidationMessage =
    (isDOBInvalid && isUnder16 && "You must be 16 years old and above.") ||
    (isDOBInvalid && !isUnder16 && "Please enter your date of birth.") ||
    "";

  const isTelephoneInvalid =
    personalDetailsErrors.mobileTelephone &&
    personalDetailsErrors.mobileTelephone?.length > 0;
  const isTelephoneInvalidFormat =
    isTelephoneInvalid &&
    personalDetailsErrors.mobileTelephone[0] ===
      "Mobile telephone must be valid";
  const telephoneValidationMessage =
    (isTelephoneInvalid &&
      isTelephoneInvalidFormat && ["Please enter a valid mobile number."]) ||
    (isTelephoneInvalid &&
      !isTelephoneInvalidFormat && ["Please enter your mobile number."]) ||
    [];

  const isMatchedAddressesNotNil = matchedAddressList !== undefined;
  const hasMatchedAddresses =
    isMatchedAddressesNotNil && matchedAddressList.length > 0;

  const formContentRef = useRef(null);
  const addressListRef = useRef(null);
  const fieldRefs = useRef({});

  useEffect(() => {
    if (
      shouldShowAddresses &&
      addressListRef.current &&
      formContentRef.current
    ) {
      const addressListPosition = addressListRef.current.offsetTop;
      formContentRef.current.scrollTo({
        top: addressListPosition,
        behavior: "smooth",
      });
    }
  }, [shouldShowAddresses, matchedAddressList]);

  useEffect(() => {
    if (addressLine1 && postcode) {
      showManualAddress();
    }
  }, []);

  const handleFormSubmit = (e) => {
    e.preventDefault();
    handleSubmitPersonalDetails(fieldRefs);
  };

  return (
    <form
      className={`col-sm-12 col-lg-6 mt-2 mt-lg-4 mb-2 ${style.form}`}
      onSubmit={handleFormSubmit}
    >
      <div ref={formContentRef}>
        <div
          className="form-group"
          ref={(el) => {
            fieldRefs.current.firstName = el;
          }}
        >
          <label htmlFor="firstName">First name*</label>
          <TextFieldGroup
            value={firstName}
            onChange={(e) => {
              setPersonalDetailsState("firstName", e.target.value);
            }}
            classes="register_field"
            validationMessages={personalDetailsErrors?.firstName}
            validationMessageIsRed
          />
        </div>
        <div
          className="form-group"
          ref={(el) => {
            fieldRefs.current.lastName = el;
          }}
        >
          <label htmlFor="firstName">Last name*</label>
          <TextFieldGroup
            value={lastName}
            onChange={(e) => {
              setPersonalDetailsState("lastName", e.target.value);
            }}
            classes="register_field"
            validationMessages={personalDetailsErrors?.lastName}
            validationMessageIsRed
          />
        </div>
        <div
          className="row"
          ref={(el) => {
            fieldRefs.current.birthDay = el;
            fieldRefs.current.bierthMonth = el;
            fieldRefs.current.birtYear = el;
          }}
        >
          <DOB
            dayOfBirth={birthDay}
            monthOfBirth={birthMonth}
            yearOfBirth={birthYear}
            handleChange={setPersonalDetailsState}
            isInvalid={isDOBInvalid}
            oneRowOnMobile={true}
          />
          {isDOBInvalid && (
            <div className="col-md-12">
              <p className="text_invalid fontSize-12" style={{ color: "red" }}>
                {dobValidationMessage}
              </p>
            </div>
          )}
        </div>
        <div
          className="form-group mt-1"
          ref={(el) => {
            fieldRefs.current.mobileTelephone = el;
          }}
        >
          <label htmlFor="mobileTelephone">Mobile*</label>
          <TextFieldGroup
            value={mobileTelephone}
            onChange={(e) => {
              setPersonalDetailsState("mobileTelephone", e.target.value);
            }}
            classes="register_field"
            validationMessages={telephoneValidationMessage}
            validationMessageIsRed
          />
        </div>
        {!isManualAddress && (
          <div
            className={"form-group"}
            ref={(el) => {
              fieldRefs.current.postcode = el;
            }}
          >
            <ContactPostcode
              memberPostcode={postcode}
              handleChange={setPersonalDetailsState}
              canFindAddress={canFindAddress}
              getMatchedAddress={getMatchedAddress}
            />
            {postcodeError && shouldShowAddresses && (
              <div
                className="text_invalid fontSize-12"
                style={{ color: "red" }}
              >
                Postcode entered is not recognised, please enter address
                manually.
              </div>
            )}
            {personalDetailsErrors.postcode &&
              Array.isArray(personalDetailsErrors.postcode) &&
              personalDetailsErrors.postcode.length > 0 && (
                <div
                  className="text_invalid fontSize-12"
                  style={{ color: "red" }}
                >
                  {personalDetailsErrors.postcode[0]}
                </div>
              )}
            {shouldShowAddresses &&
              isMatchedAddressesNotNil &&
              matchedAddressList.length === 0 && (
                <div
                  className="text_invalid fontSize-12"
                  style={{ color: "red" }}
                >
                  Postcode not found. Please change your postcode or enter your
                  address manually.
                </div>
              )}

            <div className={`mt-1 anchor-underline`}>
              <button
                id="btn-manual-address"
                className="btn-plain register_link anchor-underline p-0"
                onClick={showManualAddress}
              >
                Enter address manually
              </button>
            </div>
          </div>
        )}
        {shouldShowAddresses && hasMatchedAddresses && !postcodeError && (
          <div ref={addressListRef}>
            <div>
              <p>Please select your address from the list below:</p>
            </div>
            <div className={style.location__addressListContainer}>
              <AddressSelectList
                onAddressSelect={onAddressSelect}
                selectedIndex={selectedAddressIndex}
                addresses={matchedAddressList}
                showAddressOnSelect={true}
                showManualAddressFunc={showManualAddress}
              />
              <Button
                onClick={showManualAddress}
                classes={style.location__notListedBtn}
              >
                My address isn&apos;t listed here
              </Button>
            </div>
          </div>
        )}
        {isManualAddress && (
          <div className={"mt-3 mt-lg-2"}>
            <div className="anchor-underline">
              <button
                id="btn-manual-address"
                className="btn-plain register_link anchor-underline p-0"
                onClick={hideManualAddress}
                type="button"
              >
                Search for address
              </button>
            </div>
            <div className="mt-1">
              <ManualAddress
                addressLine1={addressLine1}
                addressLine2={addressLine2}
                addressLine3={addressLine3}
                town={town}
                county={county}
                postcode={postcode}
                country={country}
                handleChange={setPersonalDetailsState}
                handleChangeToShowNextStep={setPersonalDetailsState}
                errors={personalDetailsErrors}
              />
            </div>
          </div>
        )}
      </div>
      <div className={`mt-4 mb-lg-2 ${style.actions}`}>
        <Link to={navigateBackPath}>
          <button className="btn btn-outline-info text-dark mr-1" type="button">
            Back
          </button>
        </Link>
        <button className="btn btn-primary btn-block" type="submit">
          Next
        </button>
      </div>
    </form>
  );
};
export default PersonalDetails;
